// WHY: This script exists because Klaviyo's hosted back-in-stock modal
// filters its variant dropdown to only sold-out variants. On the archive
// template the merchant wants customers to subscribe to ANY variant
// (archive products have real inventory disguised as sold out), so we
// bypass Klaviyo's modal entirely and POST directly to Klaviyo's public
// client BIS endpoint with the chosen variant id. Klaviyo records it as
// a real BIS subscription. A parallel custom "Archive BIS Request" track
// event is fired so the merchant's flow (delay + inventory check) can
// target it without waiting on Klaviyo's standard BIS metric.
//
// The modal is a native <dialog> opened with showModal(): the browser
// paints it in the top layer, so it always centers on the viewport and is
// immune to ancestor transform/overflow/z-index. Escape-to-close and focus
// handling are native, so this script only manages open, backdrop-close,
// and the Klaviyo POSTs.
(function () {
  "use strict";

  var KLAVIYO_ENDPOINT =
    "https://a.klaviyo.com/client/back-in-stock-subscriptions/";
  var KLAVIYO_REVISION = "2024-06-15";
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  var NS = (window.__sp5derArchiveBis = window.__sp5derArchiveBis || {});
  if (NS.initialized) return;
  NS.initialized = true;

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function openModal(productId) {
    var modal = document.getElementById("archive-bis-modal-" + productId);
    if (!modal || typeof modal.showModal !== "function") return;
    resetModalState(modal);
    modal.showModal();
  }

  function closeModal(modal) {
    if (modal && typeof modal.close === "function") modal.close();
  }

  function resetModalState(modal) {
    var successEl = qs(modal, "[data-archive-bis-success]");
    var errorEl = qs(modal, "[data-archive-bis-error]");
    var submitBtn = qs(modal, "[data-archive-bis-submit]");
    var label = qs(modal, "[data-archive-bis-submit-label]");
    if (successEl) successEl.hidden = true;
    if (errorEl) errorEl.hidden = true;
    if (submitBtn) submitBtn.disabled = false;
    if (label) label.textContent = "Notify me";
  }

  function showError(modal, message) {
    var errorEl = qs(modal, "[data-archive-bis-error]");
    if (errorEl) {
      errorEl.textContent =
        message || "Something went wrong. Please try again.";
      errorEl.hidden = false;
    }
    var submitBtn = qs(modal, "[data-archive-bis-submit]");
    if (submitBtn) submitBtn.disabled = false;
    var label = qs(modal, "[data-archive-bis-submit-label]");
    if (label) label.textContent = "Notify me";
  }

  function showSuccess(modal) {
    var successEl = qs(modal, "[data-archive-bis-success]");
    if (successEl) successEl.hidden = false;
    var errorEl = qs(modal, "[data-archive-bis-error]");
    if (errorEl) errorEl.hidden = true;
    setTimeout(function () {
      closeModal(modal);
    }, 2000);
  }

  function buildCheckoutUrl(shopUrl, variantId) {
    var base = (shopUrl || "").replace(/\/$/, "");
    return base + "/cart/" + variantId + ":1?return_to=/checkout";
  }

  function submitSubscription(modal, email, selectedOption) {
    var variantId = selectedOption.getAttribute("data-variant-id");
    var variantTitle = selectedOption.getAttribute("data-variant-title");
    var inventoryQtyRaw = selectedOption.getAttribute(
      "data-variant-inventory-quantity",
    );
    var inventoryQty = parseInt(inventoryQtyRaw, 10);
    if (isNaN(inventoryQty)) inventoryQty = 0;
    var inventoryPolicy =
      selectedOption.getAttribute("data-variant-inventory-policy") || "";

    var productId = modal.getAttribute("data-product-id");
    var productTitle = modal.getAttribute("data-product-title");
    var productUrl = modal.getAttribute("data-product-url");
    var shopUrl = modal.getAttribute("data-shop-url");
    var productImage = modal.getAttribute("data-product-image");
    var companyId = modal.getAttribute("data-klaviyo-company-id");

    var submitBtn = qs(modal, "[data-archive-bis-submit]");
    var label = qs(modal, "[data-archive-bis-submit-label]");
    if (submitBtn) submitBtn.disabled = true;
    if (label) label.textContent = "Submitting...";

    var payload = {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          channels: ["EMAIL"],
          profile: {
            data: {
              type: "profile",
              attributes: { email: email },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: "$shopify:::$default:::" + variantId,
            },
          },
        },
      },
    };

    var url = KLAVIYO_ENDPOINT + "?company_id=" + encodeURIComponent(companyId);

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        // Klaviyo returns 202 for new subscriptions and also treats duplicate
        // resubscribes as success, so any 2xx should be treated as success.
        if (res.ok || res.status === 202) return res;
        return res.text().then(function (text) {
          throw new Error(text || "HTTP " + res.status);
        });
      })
      .then(function () {
        fireCustomEvent({
          companyId: companyId,
          email: email,
          variantId: variantId,
          variantTitle: variantTitle,
          productId: productId,
          productTitle: productTitle,
          productUrl: productUrl,
          productImage: productImage,
          checkoutUrl: buildCheckoutUrl(shopUrl, variantId),
          inventoryQuantity: inventoryQty,
          inventoryPolicy: inventoryPolicy,
        });
        showSuccess(modal);
      })
      .catch(function (err) {
        if (window.console && console.error) {
          console.error("[archive-bis] subscription failed", err);
        }
        showError(modal);
      });
  }

  // WHY: We POST directly to Klaviyo's public client events endpoint instead
  // of using window.klaviyo.push(['track', ...]). The push queue is consumed
  // by Klaviyo's onsite.js, which is consent-gated by Shopify Customer
  // Privacy. When a shopper declines cookies, klaviyo.push becomes a no-op
  // and the custom event silently drops. The /client/events/ REST endpoint
  // mirrors the BIS subscription path: company_id in query string, no SDK,
  // no consent dependency. The shopper has explicitly submitted the modal,
  // so the transactional event is justified.
  function fireCustomEvent(data) {
    var url =
      "https://a.klaviyo.com/client/events/?company_id=" +
      encodeURIComponent(data.companyId);
    var payload = {
      data: {
        type: "event",
        attributes: {
          properties: {
            variant_id: data.variantId,
            variant_title: data.variantTitle,
            product_id: data.productId,
            product_title: data.productTitle,
            product_url: data.productUrl,
            image_url: data.productImage,
            checkout_url: data.checkoutUrl,
            inventory_quantity: data.inventoryQuantity,
            inventory_policy: data.inventoryPolicy,
            subscribed_at: new Date().toISOString(),
          },
          metric: {
            data: {
              type: "metric",
              attributes: { name: "Archive BIS Request" },
            },
          },
          profile: {
            data: {
              type: "profile",
              attributes: { email: data.email },
            },
          },
        },
      },
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
    }).then(
      function (res) {
        if (!res.ok && res.status !== 202) {
          if (window.console && console.warn) {
            console.warn("[archive-bis] custom event non-2xx", res.status);
          }
        }
      },
      function (err) {
        if (window.console && console.warn) {
          console.warn("[archive-bis] custom event failed", err);
        }
      },
    );
  }

  function handleSubmit(event) {
    var form = event.target;
    if (!form.matches("[data-archive-bis-form]")) return;
    event.preventDefault();

    var modal = form.closest("[data-archive-bis-modal]");
    if (!modal) return;

    resetModalState(modal);

    var emailInput = qs(modal, "[data-archive-bis-email]");
    var select = qs(modal, "[data-archive-bis-variant-select]");
    var email = emailInput ? emailInput.value.trim() : "";
    var selectedOption = select && select.options[select.selectedIndex];

    if (!EMAIL_RE.test(email)) {
      showError(modal, "Please enter a valid email address.");
      if (emailInput) emailInput.focus();
      return;
    }
    if (!selectedOption || !selectedOption.getAttribute("data-variant-id")) {
      showError(modal, "Please pick a size.");
      return;
    }

    submitSubscription(modal, email, selectedOption);
  }

  function handleDocumentClick(event) {
    var opener = event.target.closest("[data-archive-bis-open]");
    if (opener) {
      event.preventDefault();
      openModal(opener.getAttribute("data-archive-bis-open"));
      return;
    }

    // Backdrop click: a click on the dialog's ::backdrop dispatches to the
    // dialog element itself, so target === the modal means the backdrop.
    var modalEl = event.target.closest("[data-archive-bis-modal]");
    if (modalEl && event.target === modalEl) {
      closeModal(modalEl);
      return;
    }

    var closer = event.target.closest("[data-archive-bis-close]");
    if (closer) {
      event.preventDefault();
      closeModal(closer.closest("[data-archive-bis-modal]"));
    }
  }

  document.addEventListener("click", handleDocumentClick);
  document.addEventListener("submit", handleSubmit);
})();
